/* eslint-disable camelcase */
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from '@smals/ngx-configuration-service';
import { jwtDecode } from 'jwt-decode';
import { KeycloakService } from 'keycloak-angular';
import { first, withLatestFrom } from 'rxjs/operators';
import { SsinDialogComponent } from 'src/app/ui-components/ssin-dialog/ssin-dialog.component';
import { TokenPerson, TokenProfessional, TokenUserProfile, UserToken } from 'vidis-common';
import {
  clearAllPatientMedicalData,
  setPatientAction,
  setPatientSsinAction,
  setUserAction
} from '../../store/actions/user.action';
import { selectUserIsHealthcareWorker, selectUserStateRedirectUrl } from '../../store/selectors/user.selector';
import { UserNoAccessDialogComponent } from '../../ui-components/user-no-access-dialog/user-no-access-dialog.component';
import { fetchDiaryNotesCountAction } from '../../store/actions/diary-notes.action';
import { fetchMedicationScheme } from '../../store/actions/medication.action';
import { PrescriptionsApiActions } from '../../store/actions/prescriptions.action';
import { DeliveredMedicationsApiActions } from '../../store/actions/delivered-medication.action';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private keycloakService: KeycloakService,
    private config: ConfigurationService,
    private translate: TranslateService,
    private store: Store,
    private dialog: MatDialog,
    private localStorageService: LocalStorageService
  ) {
  }

  async initializeUserToken(patientSsin: string): Promise<void> {
    const token = await this.keycloakService.getToken();
    const decodedTokenFull = jwtDecode<UserToken>(token);
    const userProfileData: TokenUserProfile = decodedTokenFull.userProfile;
    const lastName = userProfileData.lastName;
    const firstName = userProfileData.firstName;
    const role = this.getProfessionalRole(userProfileData);
    const orgRole = userProfileData?.organizations?.length > 0 && !!userProfileData?.organizations[0]?.pharmacy ? 'pharmacist' : undefined;
    const org = userProfileData?.organizations?.length && orgRole !== undefined ? userProfileData?.organizations[0] : undefined;
    const isHealthcareWorker = this.isProfessional(decodedTokenFull);

    this.store.dispatch(setUserAction({
      user: {
        firstName: firstName,
        ssin: userProfileData.ssin,
        lastName,
        role: orgRole ? orgRole : role,
        organization: org && orgRole ? org : undefined,
        professional: this.getProfessionalRole(userProfileData) ?
          (userProfileData[this.getProfessionalRole(userProfileData)] as TokenProfessional) : undefined,
        mandator: userProfileData?.mandators?.length > 0,
        isHealthcareWorker: isHealthcareWorker,
      }
    }));

    const children: TokenPerson[] = userProfileData.children;
    const mandators: TokenPerson[] = userProfileData.mandators;

    if (children?.length > 0) {
      this.setPatient(children[0]);
    } else if (mandators?.length > 0) {
      this.setPatient(mandators[0]);
    } else if (!isHealthcareWorker) {
      this.setPatient({firstName: firstName, lastName: lastName, ssin: userProfileData.ssin});
    } else if (patientSsin) {
      this.setPatient({ssin: patientSsin});
    } else {
      this.openSsinDialog(true);
    }
  }

  openSsinDialog(isLogout: boolean): void {
    this.dialog.open(SsinDialogComponent, {
      data: {isLogout: isLogout},
      disableClose: true
    })
      .beforeClosed()
      .subscribe(result => {
        if (result?.ssin) {
          this.changePatientSsin(result.ssin);
          this.localStorageService.setItemWithExpiracy('patientSsin',result.ssin, 3600000);

        } else if (isLogout) {
          this.logoutUser();
        }
      });
  }

  openHasNoAccessDialog(): void {
    const defaultLang = this.translate.currentLang || 'nl';
    this.translate.setDefaultLang(defaultLang);
    this.dialog.open(UserNoAccessDialogComponent, {
      disableClose: true
    })
      .beforeClosed()
      .subscribe(() => {
        this.logoutUser();
      });
  }

  logoutUser(): void {
    this.store.select(selectUserStateRedirectUrl).pipe(
      withLatestFrom(this.store.select(selectUserIsHealthcareWorker)),
      first()
    ).subscribe(([redirectUrl, isHealthcareWorker]) => {
      const defaultLang = this.translate.currentLang || 'nl';
      const redirectURLParam = isHealthcareWorker ? 'rizivURL' : 'myHealthUrl';
      if (!redirectUrl) {
        redirectUrl = this.config.getEnvironmentVariable(redirectURLParam)[defaultLang];
      }
      void this.keycloakService.logout(redirectUrl);
    });
  }

  private setPatient(patient: TokenPerson): void {
    this.store.dispatch(setPatientAction({
      patient: {firstName: patient.firstName, lastName: patient.lastName, ssin: patient.ssin}
    }));
  }

  private changePatientSsin(patientSsin: string): void {
    this.store.dispatch(setPatientSsinAction({ssin: patientSsin}));
    this.store.dispatch(clearAllPatientMedicalData());
    this.store.dispatch(fetchDiaryNotesCountAction());
    this.store.dispatch(PrescriptionsApiActions.fetchCount());
    this.store.dispatch(DeliveredMedicationsApiActions.fetchCount());
    this.store.dispatch(fetchMedicationScheme());
  }

  private getProfessionalRole(decodedToken: TokenUserProfile): string {
    let userRoles: string[] = ['physician', 'nurse', 'midwife', 'dentist'];
    let returnRole: string;
    userRoles.forEach(role => {
      if (decodedToken[role]) {
        returnRole = role;
      }
    });
    return returnRole ? returnRole : 'patient';
  }

  private isProfessional(decodedToken: UserToken): boolean {
    if (decodedToken.azp) {
      return decodedToken.azp.includes('nihdi-vidis-caregiver');
    }
    return false;
  }

  isCaregiver(decodedTokenFull: UserToken): boolean {
    let userRoles: string[] = ['physician', 'nurse', 'midwife', 'dentist', 'pharmacist'];
    const userProfileData: TokenUserProfile = decodedTokenFull.userProfile;
    const role = this.getProfessionalRole(userProfileData);
    const orgRole = userProfileData?.organizations?.length > 0 && !!userProfileData?.organizations[0]?.pharmacy ? 'pharmacist' : undefined;
    const roleCaregiver: string = orgRole ? orgRole : role;

    return this.resourceAccessContainsCaregiver(decodedTokenFull) || userRoles.includes(roleCaregiver);
  }

  private resourceAccessContainsCaregiver(decodedToken: UserToken): boolean {
    if (decodedToken.resource_access) {
      return !!decodedToken.resource_access['nihdi-vidis-caregiver'];
    }

    return false;
  }
}

